import { StringFormatter } from '@domains/subscription/priv/format'
import ErrorIcon from '@mui/icons-material/Error'
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined'
import { Button, FormControlLabel, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { type Amount, type PaymentFrequency, PaymentFrequencyType, type SubscriptionFee } from '../api/api'
import { BorderedStack } from '../components/BorderedStack'
import { ConfirmSelectSubscriptionPlan, type ConfirmSelectSubscriptionPlanProps } from '../components/ConfirmSelectSubscriptionPlan'
import { SelectSubscriptionPlan } from '../components/SelectSubscriptionPlan'

interface Template {
  id: string
  name: string
  externalName: string
}

interface SelectTemplateOptions {
  templateId: string
  keepCustomerOverrides: boolean
}

export interface CustomerSubscriptionPlanProps {
  customerTemplate: Template | undefined
  subscriptionFee: SubscriptionFee | undefined
  templateRelations: Template[]
  allTemplates: Template[]
  onSelectTemplate: (options: SelectTemplateOptions) => void
  onSetSubscriptionFee: (fee: Amount) => void
  onDeleteSubscriptionFee: () => void
  isLoading: boolean
  isPatching: boolean
  isDeleting: boolean
  isError?: boolean
}

interface FormData {
  customerTemplate: Template | undefined
  subscriptionFee: SubscriptionFee | undefined
}

export const CustomerSubscriptionPlan = ({
  customerTemplate,
  templateRelations,
  allTemplates,
  subscriptionFee,
  onSelectTemplate,
  onSetSubscriptionFee,
  onDeleteSubscriptionFee,
  isLoading,
  isPatching,
  isError,
  isDeleting,
}: CustomerSubscriptionPlanProps) => {
  const [confirmModalData, setConfirmModalData] = useState<{ open: boolean; subscriptionFee?: SubscriptionFee }>({
    open: false,
    subscriptionFee: undefined,
  })
  const [toggleAdminMode, setToggleAdminMode] = useState(false)

  const form = useForm<FormData>({
    values: {
      subscriptionFee,
      customerTemplate,
    },
  })

  const isCustomerWithoutPlan = !customerTemplate && !isLoading && !isError
  const isAdminMode = toggleAdminMode || isCustomerWithoutPlan

  const { templates } = getTemplateRelations({
    templateRelations,
    allTemplates,
    customerTemplate: customerTemplate,
    isAdminMode,
  })

  const templateOptions = templates.map((plan) => ({
    value: plan.id,
    label: isAdminMode ? plan.name : formatter.format(plan.externalName),
  }))

  const subscriptionFeeValue = useWatch({ control: form.control, name: 'subscriptionFee' })
  const selectedCustomerTemplateId = useWatch({ control: form.control, name: 'customerTemplate.id' })
  const selectedCustomerTemplate = templates.find((t) => t.id === selectedCustomerTemplateId)

  const handleChangeAdminMode = (_: React.ChangeEvent<HTMLInputElement>, isAdmin: boolean) => {
    if (!isAdmin && !isCustomerWithoutPlan) {
      form.setValue('customerTemplate', customerTemplate)
    }

    setToggleAdminMode(isAdmin)
  }

  const confirmSave: ConfirmSelectSubscriptionPlanProps['onConfirm'] = ({ keepCustomerOverrides }) => {
    if (selectedCustomerTemplate) {
      setConfirmModalData({ open: false, subscriptionFee: undefined })
      onSelectTemplate({ templateId: selectedCustomerTemplate.id, keepCustomerOverrides })
    }

    if (confirmModalData.subscriptionFee) {
      onSetSubscriptionFee(confirmModalData.subscriptionFee.subscriptionFee)
    }
  }

  const handleSaveTemplate = (subscriptionFee: SubscriptionFee | undefined) => {
    if (!selectedCustomerTemplate) return

    if (isCustomerWithoutPlan) {
      onSelectTemplate({ templateId: selectedCustomerTemplate.id, keepCustomerOverrides: true })

      if (subscriptionFee) {
        onSetSubscriptionFee(subscriptionFee?.subscriptionFee)
      }
    }

    setConfirmModalData({ open: true, subscriptionFee })
  }

  const handleSave = (data: FormData) => {
    const isTemplateChanged = Boolean(form.formState.dirtyFields.customerTemplate)
    const isSubscriptionFeeChanged = Boolean(form.formState.dirtyFields.subscriptionFee)

    const changedSubscriptionFee = isSubscriptionFeeChanged ? data.subscriptionFee : undefined

    if (isTemplateChanged) {
      handleSaveTemplate(changedSubscriptionFee)
    } else if (changedSubscriptionFee) {
      onSetSubscriptionFee(changedSubscriptionFee.subscriptionFee)
    }
  }

  const isSaveDisabled = !form.formState.isDirty || isError || isPatching

  const templateName = customerTemplate ? customerTemplate.name : 'No subscription plan'

  return (
    <BorderedStack
      sx={{
        flex: 1,
        padding: 2,
        gap: 2,
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack
          sx={{
            gap: 0.5,
          }}
        >
          <Typography variant="p2Medium">Subscription plan</Typography>
          <Loader isLoading={isLoading} loader={SelectSubscriptionPlan.TemplateSkeleton}>
            <Stack>
              <Typography variant="p4Regular" color="textSecondary">
                {templateName}
              </Typography>
              {isError && (
                <Typography variant="p4Regular" color="error">
                  Something went wrong loading the subscription plan
                </Typography>
              )}
            </Stack>
          </Loader>
        </Stack>
        <FormControlLabel
          sx={{ margin: 0, alignItems: 'flex-end' }}
          labelPlacement="top"
          control={<Switch checked={isAdminMode} onChange={handleChangeAdminMode} />}
          label={
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              <Tooltip
                title="Admin mode allows you to see and select all available subscription plans, even those that are outside of the customers intended and eligible plans. Use with caution"
                placement="top"
              >
                <Typography
                  color="textSecondary"
                  sx={{
                    lineHeight: 0,
                  }}
                >
                  <InfoOutlineIcon fontSize="inherit" />
                </Typography>
              </Tooltip>
              <Typography variant="p4Regular" color="textSecondary">
                Admin mode
              </Typography>
            </Stack>
          }
        />
      </Stack>
      <Loader isLoading={isLoading} loader={SelectSubscriptionPlan.SelectSkeleton}>
        <Controller
          control={form.control}
          name="customerTemplate.id"
          render={({ field }) => (
            <SelectSubscriptionPlan disabled={isError} value={field.value} options={templateOptions} onSelect={field.onChange} />
          )}
        />
      </Loader>
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="p2Medium">Fixed fee</Typography>
            {subscriptionFeeValue?.isOverridden && (
              <Tooltip placement="top" title="This value has been changed from the default on the subscription plan">
                <ErrorIcon data-testid="override-warning" fontSize="inherit" color="warning" />
              </Tooltip>
            )}
          </Stack>
          {subscriptionFeeValue?.paymentFrequency && (
            <Typography color="secondary" variant="p4Regular">
              billed {formatBillingFrequency(subscriptionFeeValue.paymentFrequency)}
            </Typography>
          )}
        </Stack>
        <Loader isLoading={isLoading} loader={SelectSubscriptionPlan.SelectSkeleton}>
          <Controller
            control={form.control}
            name="subscriptionFee.subscriptionFee.value"
            render={({ field }) => (
              <TextField
                disabled={isError}
                type="number"
                value={field.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.valueAsNumber)}
                slotProps={{
                  input: { endAdornment: subscriptionFeeValue?.subscriptionFee.currency },
                  htmlInput: { 'aria-label': 'Subscription fee' },
                }}
              />
            )}
          />
        </Loader>
      </Stack>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Button
          loading={isPatching}
          disabled={isSaveDisabled}
          variant="contained"
          onClick={form.handleSubmit(handleSave)}
          size="medium"
          data-testid="save-button"
          sx={{ alignSelf: 'flex-start' }}
        >
          Save
        </Button>
        {subscriptionFeeValue?.isOverridden && (
          <Button size="medium" loading={isDeleting} disabled={isError} color="error" onClick={onDeleteSubscriptionFee}>
            Delete fee override
          </Button>
        )}
      </Stack>
      <ConfirmSelectSubscriptionPlan
        plan={formatter.format(selectedCustomerTemplate?.externalName ?? '')}
        open={confirmModalData.open}
        onConfirm={confirmSave}
        onCancel={() => setConfirmModalData({ open: false, subscriptionFee: undefined })}
      />
    </BorderedStack>
  )
}

export const formatBillingFrequency = (paymentFrequency: PaymentFrequency) => {
  switch (paymentFrequency.type) {
    case PaymentFrequencyType.DAILY: {
      return 'daily'
    }

    case PaymentFrequencyType.MONTHLY: {
      return `monthly on the ${dayjs().date(paymentFrequency.dayOfMonth).format('Do')}`
    }

    case PaymentFrequencyType.WEEKLY: {
      return `weekly on ${paymentFrequency.dayOfWeek}`
    }

    case PaymentFrequencyType.YEARLY: {
      return `yearly on ${dayjs()
        .startOf('year')
        .add(paymentFrequency.dayOfYear - 1, 'day')
        .format('Do [day of] MMMM')}`
    }
  }
}

export const formatter = new StringFormatter({
  normalizers: [new StringFormatter.normalizers.enum(), new StringFormatter.normalizers.uppercase()],
  format: new StringFormatter.formats.capitalized(),
})

interface UseTemplateRelationsParams {
  templateRelations: Template[]
  allTemplates: Template[]
  customerTemplate: Template | undefined
  isAdminMode: boolean
}

interface UseTemplateRelationsResult {
  templates: Template[]
}

const getTemplateRelations = ({
  templateRelations,
  allTemplates,
  customerTemplate,
  isAdminMode,
}: UseTemplateRelationsParams): UseTemplateRelationsResult => {
  const templates = isAdminMode || !customerTemplate ? allTemplates : templateRelations

  return { templates }
}

interface LoaderProps {
  isLoading: boolean
  loader: React.ComponentType
  children: React.ReactNode
}

const Loader = ({ isLoading, loader: Loader, children }: LoaderProps) => {
  if (isLoading) return <Loader />

  return <>{children}</>
}
